import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase';
import { leadKeys } from '@/features/leads/useLeads';
import type { LeadStage } from '@/types/app';

export const taskKeys = {
  all: ['tasks'] as const,
  mine: (scope: string) => ['tasks', 'mine', scope] as const,
  alerts: () => ['alerts'] as const,
  noNextAction: () => ['alerts', 'no-next-action'] as const,
};

export interface TaskRow {
  id: string;
  lead_id: string;
  owner_id: string;
  due_at: string;
  type: string;
  note: string | null;
  status: 'open' | 'done' | 'cancelled';
  is_overdue: boolean;
  escalation_level: number;
  leads: { full_name: string; phone_primary: string; stage: string } | null;
}

// prettier-ignore
const TASK_COLUMNS = 'id, lead_id, owner_id, due_at, type, note, status, is_overdue, escalation_level, leads(full_name, phone_primary, stage)';

/**
 * My Tasks and the Overdue queue.
 *
 * No owner filter: RLS already decides whose tasks exist for the caller, so a rep
 * sees theirs and a team leader sees the team's. Filtering by auth.uid() here would
 * quietly break the team leader's queue.
 */
export function useMyTasks(scope: 'open' | 'overdue') {
  return useQuery({
    queryKey: taskKeys.mine(scope),
    queryFn: async (): Promise<TaskRow[]> => {
      let query = supabase.from('tasks').select(TASK_COLUMNS).eq('status', 'open');

      if (scope === 'overdue') query = query.eq('is_overdue', true);

      const { data, error } = await query.order('due_at', { ascending: true }).limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as TaskRow[];
    },
    // Due dates pass while the screen is open; a stale queue is worse than a refetch.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export interface AlertRow {
  alert: 'uncontacted' | 'overdue_task' | 'hot_no_activity' | 'sla_breached' | 'stalled';
  lead_id: string;
  full_name: string;
  owner_id: string | null;
  team_id: string | null;
  since: string;
  due_at: string | null;
  severity: number;
}

/** The management alert queue (#36). */
export function useAlerts() {
  return useQuery({
    queryKey: taskKeys.alerts(),
    queryFn: async (): Promise<AlertRow[]> => {
      const { data, error } = await supabase
        .from('v_lead_alerts')
        .select('alert, lead_id, full_name, owner_id, team_id, since, due_at, severity')
        .order('severity', { ascending: false })
        .order('since', { ascending: true })
        .limit(300);

      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export interface NoNextActionRow {
  lead_id: string;
  full_name: string;
  owner_id: string | null;
  stage: LeadStage;
  last_activity_at: string | null;
}

/** #11 / #42: active leads nobody has committed to contacting again. */
export function useLeadsWithoutNextAction() {
  return useQuery({
    queryKey: taskKeys.noNextAction(),
    queryFn: async (): Promise<NoNextActionRow[]> => {
      const { data, error } = await supabase
        .from('v_leads_without_next_action')
        .select('lead_id, full_name, owner_id, stage, last_activity_at')
        .limit(300);

      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      leadId,
      dueAt,
      type,
      note,
    }: {
      leadId: string;
      dueAt: string;
      type?: string;
      note?: string;
    }) =>
      rpc('create_task', {
        p_lead_id: leadId,
        p_due_at: dueAt,
        p_type: type ?? 'call',
        p_note: note ?? null,
      }),
    onSuccess: (_d, { leadId }) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.all });
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.alerts() });
    },
  });
}

// The optional fields spell out `| undefined` because exactOptionalPropertyTypes is
// on: the caller builds this object by spreading one of two branches, so the keys
// are present-but-undefined rather than absent.
export interface CompleteTaskInput {
  taskId: string;
  leadId: string;
  outcomeNote?: string | undefined;
  nextDueAt?: string | null | undefined;
  nextType?: string | undefined;
  nextNote?: string | undefined;
  closeLead?: boolean | undefined;
  lostReason?: string | undefined;
}

/**
 * Closing an action. The database demands the next one or an explicit close of the
 * lead (#11), so the form collects both and lets the server be the judge.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CompleteTaskInput) =>
      rpc('complete_task', {
        p_task_id: input.taskId,
        p_outcome_note: input.outcomeNote ?? null,
        p_next_due_at: input.nextDueAt ?? null,
        p_next_type: input.nextType ?? 'call',
        p_next_note: input.nextNote ?? null,
        p_close_lead: input.closeLead ?? false,
        p_lost_reason: input.lostReason ?? null,
      }),
    onSuccess: (_d, { leadId }) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.all });
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
      void queryClient.invalidateQueries({ queryKey: taskKeys.alerts() });
    },
  });
}
